'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'

import {
  AccessDeniedModal,
} from '@/components/access/AccessDeniedModal'

import type {
  AccessResult,
} from '@/components/access/types'

import type {
  OperationalCapabilityId,
} from '@/lib/promanity/operational-capacity'

type OperationalGateOptions = {
  capabilityId: OperationalCapabilityId
  title?: string
}

type PendingAction = {
  action: () => void | Promise<void>
  options: OperationalGateOptions
}

type OperationalCapacityGateContextValue = {
  check: (
    options: OperationalGateOptions
  ) => Promise<boolean>

  guard: (
    action: () => void | Promise<void>,
    options: OperationalGateOptions
  ) => Promise<boolean>

  checking: boolean
}

const OperationalCapacityGateContext =
  createContext<OperationalCapacityGateContextValue | null>(
    null
  )

const POLL_INTERVAL_MS = 2_000
const POLL_TIMEOUT_MS = 120_000

export function OperationalCapacityGateProvider({
  sellerId,
  children,
}: {
  sellerId?: number | null
  children: ReactNode
}) {
  const [
    access,
    setAccess,
  ] = useState<AccessResult | null>(
    null
  )

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    title,
    setTitle,
  ] = useState(
    'Activa la capacidad operativa de AIA'
  )

  const [
    checking,
    setChecking,
  ] = useState(false)

  const pendingRef =
    useRef<PendingAction | null>(
      null
    )

  const executingRef =
    useRef(false)

  const pollStartedAtRef =
    useRef<number | null>(
      null
    )

  const checkAccess =
    useCallback(
      async (
        options: OperationalGateOptions,
        showModalOnDenied: boolean
      ) => {
        if (!sellerId) {
          if (showModalOnDenied) {
            setAccess({
              allowed: false,
              reason: 'product',
              productIds: [],
            } as AccessResult)

            setOpen(true)
          }

          return false
        }

        const response =
          await fetch(
            `/api/access/operational-capacity?seller_id=${sellerId}&capability_id=${encodeURIComponent(
              options.capabilityId
            )}`,
            {
              cache: 'no-store',
            }
          )

        const data =
          (await response.json()) as AccessResult

        if (data.allowed) {
          return true
        }

        if (showModalOnDenied) {
          setAccess(data)
          setOpen(true)
        }

        return false
      },
      [
        sellerId,
      ]
    )

  const check =
    useCallback(
      async (
        options: OperationalGateOptions
      ) => {
        if (options.title) {
          setTitle(
            options.title
          )
        }

        try {
          setChecking(true)

          return await checkAccess(
            options,
            true
          )
        } catch (error) {
          console.error(
            '[OperationalCapacityGate] access check failed:',
            error
          )

          setAccess({
            allowed: false,
            reason: 'product',
            productIds: [],
          } as AccessResult)

          setOpen(true)

          return false
        } finally {
          setChecking(false)
        }
      },
      [
        checkAccess,
      ]
    )

  const executePending =
    useCallback(
      async () => {
        if (
          executingRef.current ||
          !pendingRef.current
        ) {
          return
        }

        const pending =
          pendingRef.current

        executingRef.current = true
        pendingRef.current = null

        try {
          setOpen(false)
          setAccess(null)

          await pending.action()
        } catch (error) {
          console.error(
            '[OperationalCapacityGate] pending action failed:',
            error
          )
        } finally {
          executingRef.current = false
          pollStartedAtRef.current = null
        }
      },
      []
    )

  const guard =
    useCallback(
      async (
        action: () => void | Promise<void>,
        options: OperationalGateOptions
      ) => {
        pendingRef.current = {
          action,
          options,
        }

        const allowed =
          await check(options)

        if (!allowed) {
          pollStartedAtRef.current =
            Date.now()

          return false
        }

        await executePending()

        return true
      },
      [
        check,
        executePending,
      ]
    )

  useEffect(() => {
    if (!pendingRef.current) {
      return
    }

    let cancelled = false
    let timeoutId:
      | number
      | null = null

    async function poll() {
      if (
        cancelled ||
        !pendingRef.current
      ) {
        return
      }

      const startedAt =
        pollStartedAtRef.current ||
        Date.now()

      pollStartedAtRef.current =
        startedAt

      if (
        Date.now() -
          startedAt >
        POLL_TIMEOUT_MS
      ) {
        pendingRef.current = null
        pollStartedAtRef.current = null
        setChecking(false)

        return
      }

      try {
        setChecking(true)

        const allowed =
          await checkAccess(
            pendingRef.current.options,
            false
          )

        if (allowed) {
          await executePending()
          return
        }
      } catch (error) {
        console.error(
          '[OperationalCapacityGate] polling failed:',
          error
        )
      } finally {
        setChecking(false)
      }

      if (!cancelled) {
        timeoutId =
          window.setTimeout(
            poll,
            POLL_INTERVAL_MS
          )
      }
    }

    timeoutId =
      window.setTimeout(
        poll,
        POLL_INTERVAL_MS
      )

    return () => {
      cancelled = true

      if (
        timeoutId !== null
      ) {
        window.clearTimeout(
          timeoutId
        )
      }
    }
  }, [
    access,
    checkAccess,
    executePending,
  ])

  const value =
    useMemo(
      () => ({
        check,
        guard,
        checking,
      }),
      [
        check,
        guard,
        checking,
      ]
    )

  return (
    <OperationalCapacityGateContext.Provider
      value={value}
    >
      {children}

      {access &&
        !access.allowed && (
          <Dialog
            open={open}
            onOpenChange={
              setOpen
            }
          >
            <DialogContent className="max-w-md">
              <AccessDeniedModal
                title={title}
                access={access}
                type="tool"
                mode="restricted"
                step="preview"
              />
            </DialogContent>
          </Dialog>
        )}
    </OperationalCapacityGateContext.Provider>
  )
}

export function useOperationalCapacityGate() {
  const context =
    useContext(
      OperationalCapacityGateContext
    )

  if (!context) {
    throw new Error(
      'useOperationalCapacityGate must be used within <OperationalCapacityGateProvider />'
    )
  }

  return context
}